import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Building, Lock, ToggleLeft, ToggleRight, ChevronDown, Shield, Smartphone, AlertTriangle, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { userService } from '../../services/userService';
import { apiClient } from '../../services/apiClient';
import PersonalVerificationCard from '../../components/Verification/PersonalVerificationCard';
import EnterpriseVerificationCard from '../../components/Verification/EnterpriseVerificationCard';
import PaymentSettings from './PaymentSettings';
import { useToast } from '../../components/UI/Toast';
import { Eye, EyeOff, X } from 'lucide-react';
import { buildOwnProfilePath } from '../../utils/share';

const SettingsPage = () => {
    const [activeTab, setActiveTab] = useState<'profile' | 'verification' | 'privacy' | 'general' | 'security' | 'payment'>('profile');
    const [isLoading, setIsLoading] = useState(true);

    // Password modal state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPw, setShowNewPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const [passwordVerificationCode, setPasswordVerificationCode] = useState('');
    const [passwordCodeCountdown, setPasswordCodeCountdown] = useState(0);
    const [passwordCodeSending, setPasswordCodeSending] = useState(false);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [accountDeletionVerificationCode, setAccountDeletionVerificationCode] = useState('');
    const [accountDeletionCodeCountdown, setAccountDeletionCodeCountdown] = useState(0);
    const [accountDeletionCodeSending, setAccountDeletionCodeSending] = useState(false);
    const [accountDeletionSubmitting, setAccountDeletionSubmitting] = useState(false);
    const [accountStatus, setAccountStatus] = useState('active');
    const [deletionEffectiveAt, setDeletionEffectiveAt] = useState<number | undefined>(undefined);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [userId, setUserId] = useState('');
    const { showToast } = useToast();
    const ownProfilePath = userId ? buildOwnProfilePath(userId) : '/profile';

    // Profile Form State
    const [nickname, setNickname] = useState('');
    const [bio, setBio] = useState('');
    const [tags, setTags] = useState('');
    const [contactInfo, setContactInfo] = useState({
        email: '',
        phone: '',
        wechat: '',
        location: '上海, 中国', // Mock default or fetch if available
        website: ''
    });

    // Verification State
    const [personalAuthStatus, setPersonalAuthStatus] = useState<'unverified' | 'pending' | 'verified'>('unverified');
    const [enterpriseAuthStatus, setEnterpriseAuthStatus] = useState<'unverified' | 'pending' | 'verified'>('unverified');

    // Privacy State (Mock - could be connected to API later)
    const [privacySettings, setPrivacySettings] = useState({
        publicProfile: true,
        allowMessages: true,
        showOnlineStatus: false
    });

    // General State (Mock)
    const [language, setLanguage] = useState('zh');
    const [theme, setTheme] = useState('light');

    // Fetch Profile Data
    useEffect(() => {
        const fetchProfile = async () => {
            setIsLoading(true);
            try {
                const [profileResponse, credentialsResponse] = await Promise.all([
                    userService.getUserProfile(),
                    userService.getUserCredentials()
                ]);

                if (profileResponse.success && profileResponse.data?.userProfile) {
                    const profile = profileResponse.data.userProfile;
                    setUserId(profile.userId || '');
                    setNickname(profile.displayName || profile.username || '');
                    setBio(profile.bio || '');
                    setTags(profile.creatorInfo?.tags?.join(', ') || '');
                    setAvatarUrl(profile.avatarUrl || '');
                    setContactInfo({
                        email: profile.email || '',
                        phone: profile.phone || '',
                        wechat: profile.wechat || '',
                        location: profile.location || '上海, 中国',
                        website: profile.website || ''
                    });
                    setAccountStatus(profile.status || 'active');
                    setDeletionEffectiveAt(profile.deletionEffectiveAt);

                    // Sync Verification Status from profile
                    setPersonalAuthStatus(profile.verified ? 'verified' : 'unverified');
                    // Fetch enterprise verification status
                    if (profile.accountType === 'company') {
                        try {
                            const evRes = await apiClient.get<any>('/api/v1/verification/enterprise/status');
                            const evData = (evRes.data as any)?.data || evRes.data;
                            setEnterpriseAuthStatus(evData?.status === 'verified' ? 'verified' : 'pending');
                        } catch { setEnterpriseAuthStatus('pending'); }
                    } else {
                        setEnterpriseAuthStatus('unverified');
                    }
                }

                if (credentialsResponse.success && credentialsResponse.data?.credentials) {
                    setHasPassword(credentialsResponse.data.credentials.some((credential) => credential.hasPassword));
                } else {
                    setHasPassword(false);
                }
            } catch (error) {
                console.error('Failed to fetch profile settings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (passwordCodeCountdown <= 0) return;
        const timer = setInterval(() => setPasswordCodeCountdown((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [passwordCodeCountdown]);

    useEffect(() => {
        if (accountDeletionCodeCountdown <= 0) return;
        const timer = setInterval(() => setAccountDeletionCodeCountdown((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [accountDeletionCodeCountdown]);

    const handlePersonalVerifySuccess = async () => {
        // mock the personal verification finish and call update to backend API status
        setPersonalAuthStatus('verified');
        try {
            await userService.updateUserProfile({ verified: true });
        } catch (error) {
            console.error('Failed to sync verification state', error);
        }
    };

    const handleEnterpriseVerifySuccess = async () => {
        setEnterpriseAuthStatus('pending'); // Enterprise gets pending review status
    };

    const togglePrivacy = (key: keyof typeof privacySettings) => {
        setPrivacySettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const hasBoundPhone = Boolean(contactInfo.phone?.trim());
    const pendingAccountDeletion = accountStatus === 'pending_deletion'
        && typeof deletionEffectiveAt === 'number'
        && deletionEffectiveAt > Date.now();
    const formatDeletionTime = (timestamp?: number) => {
        if (!timestamp) {
            return '';
        }
        return new Date(timestamp).toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white pt-24 pb-20 flex items-center justify-center">
                <div className="animate-pulse flex flex-col items-center">
                    <div className="h-12 w-12 bg-gray-200 rounded-full mb-4"></div>
                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white pt-24 pb-20">
            <div className="max-w-[1000px] mx-auto px-6">

                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Link to={ownProfilePath} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors">
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">通用设置</h1>
                </div>

                <div className="flex flex-col md:flex-row gap-12">

                    {/* Sidebar Navigation */}
                    <div className="w-full md:w-64 shrink-0 space-y-2">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors flex items-center gap-3 ${activeTab === 'profile' ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <User className="w-5 h-5" />
                            账户信息
                        </button>
                        <button
                            onClick={() => setActiveTab('verification')}
                            className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors flex items-center gap-3 ${activeTab === 'verification' ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Building className="w-5 h-5" />
                            认证中心
                        </button>
                        <button
                            onClick={() => setActiveTab('security')}
                            className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors flex items-center gap-3 ${activeTab === 'security' ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Shield className="w-5 h-5" />
                            账号安全
                        </button>
                        <button
                            onClick={() => setActiveTab('payment')}
                            className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors flex items-center gap-3 ${activeTab === 'payment' ? 'bg-black text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                            <CreditCard className="w-5 h-5" />
                            支付设置
                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">

                        {/* PROFILE EDIT TAB */}
                        {activeTab === 'profile' && (
                            <div className="animate-fade-in">
                                <h2 className="text-xl font-bold mb-6">账户信息</h2>
                                <div className="space-y-6 max-w-lg">
                                    {/* Avatar Display */}
                                    <div className="flex items-center gap-6">
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt="Avatar"
                                                className="w-20 h-20 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
                                                <User className="w-8 h-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">昵称</label>
                                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                            {nickname || '未设置'}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">个人简介</label>
                                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                            {bio || '未设置'}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">所在城市</label>
                                            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                                {contactInfo.location}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">个人网站</label>
                                            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                                {contactInfo.website || '未设置'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">邮箱</label>
                                            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                                {contactInfo.email || '未设置'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">微信号</label>
                                            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                                {contactInfo.wechat || '未设置'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">手机号</label>
                                            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                                {contactInfo.phone || '未设置'}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">技能标签</label>
                                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                                            {tags || '未设置'}
                                        </div>
                                    </div>

                                    <div className="pt-4">
                                        <Link
                                            to="/profile/edit"
                                            className="inline-block px-8 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
                                        >
                                            编辑个人资料
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* VERIFICATION TAB */}
                        {activeTab === 'verification' && (
                            <div className="animate-fade-in space-y-8">
                                <div>
                                    <h2 className="text-xl font-bold mb-2">实名/企业认证</h2>
                                    <p className="text-gray-500 text-sm mb-6">完成认证以解锁更多权益，企业认证需先完成个人实名认证。</p>
                                </div>

                                {/* Personal Verification */}
                                <PersonalVerificationCard
                                    status={personalAuthStatus}
                                    onVerifySuccess={handlePersonalVerifySuccess}
                                />

                                {/* Enterprise Verification */}
                                <EnterpriseVerificationCard
                                    status={enterpriseAuthStatus}
                                    isPersonalVerified={personalAuthStatus === 'verified'}
                                    onVerifySuccess={handleEnterpriseVerifySuccess}
                                />
                            </div>
                        )}

                        {/* PRIVACY TAB */}
                        {activeTab === 'privacy' && (
                            <div className="animate-fade-in">
                                <h2 className="text-xl font-bold mb-6">隐私设置</h2>
                                <div className="space-y-6 max-w-lg">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <div>
                                            <div className="font-medium text-gray-900">公开个人主页</div>
                                            <div className="text-sm text-gray-500 mt-1">关闭后，仅通过分享链接可见</div>
                                        </div>
                                        <button onClick={() => togglePrivacy('publicProfile')} className="text-black hover:opacity-80 transition-opacity">
                                            {privacySettings.publicProfile ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10 text-gray-300" />}
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <div>
                                            <div className="font-medium text-gray-900">允许陌生人私信</div>
                                            <div className="text-sm text-gray-500 mt-1">关闭后，仅限合作中的客户联系</div>
                                        </div>
                                        <button onClick={() => togglePrivacy('allowMessages')} className="text-black hover:opacity-80 transition-opacity">
                                            {privacySettings.allowMessages ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10 text-gray-300" />}
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <div>
                                            <div className="font-medium text-gray-900">显示在线状态</div>
                                            <div className="text-sm text-gray-500 mt-1">允许他人看到您的实时在线情况</div>
                                        </div>
                                        <button onClick={() => togglePrivacy('showOnlineStatus')} className="text-black hover:opacity-80 transition-opacity">
                                            {privacySettings.showOnlineStatus ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10 text-gray-300" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SECURITY TAB */}
                        {activeTab === 'security' && (
                            <div className="animate-fade-in">
                                <h2 className="text-xl font-bold mb-2">账号安全</h2>
                                <p className="text-gray-500 text-sm mb-6">管理您的安全设置，提升账号安全等级</p>
                                <div className="space-y-6 max-w-2xl">
                                    
                                    <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                                                    <Lock className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-gray-900">登录密码</h3>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {hasPassword
                                                            ? '您已设置密码，可使用密码+手机号登录'
                                                            : '您尚未设置密码，设置后可使用密码+手机号登录'}
                                                    </p>
                                                </div>
                                            </div>
                                                <button 
                                                    onClick={() => {
                                                        setNewPassword('');
                                                        setConfirmPassword('');
                                                        setPasswordVerificationCode('');
                                                        setShowPasswordModal(true);
                                                    }}
                                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    {hasPassword ? '修改密码' : '设置密码'}
                                                </button>
                                        </div>

                                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                                                    <Smartphone className="w-5 h-5 text-green-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-gray-900">绑定手机号</h3>
                                                    <p className="text-sm text-gray-500 mt-1">{contactInfo.phone ? `已绑定: ${contactInfo.phone.replace(/(\+\d{2}?)(\d{3})\d{4}(\d{4})/, '$1-$2****$3')}` : '尚未绑定手机号'}</p>
                                                </div>
                                            </div>
                                            <button className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                                更换手机
                                            </button>
                                        </div>

                                        {/* 微信账号 binding hidden — feature deferred until WeChat OAuth is wired up. */}

                                        <div className="flex items-center justify-between p-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                                                    <AlertTriangle className="w-5 h-5 text-red-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-gray-900">注销账号</h3>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {pendingAccountDeletion
                                                            ? `已申请注销，将于 ${formatDeletionTime(deletionEffectiveAt)} 生效`
                                                            : (hasBoundPhone
                                                                ? '提交短信验证码后，账号将在 3 天后注销生效'
                                                                : '请先绑定手机号后再申请注销')}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                disabled={!hasBoundPhone || pendingAccountDeletion}
                                                onClick={() => {
                                                    setAccountDeletionVerificationCode('');
                                                    setShowDeleteAccountModal(true);
                                                }}
                                                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {pendingAccountDeletion ? '注销申请中' : '申请注销'}
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* Password Modal */}
                        {showPasswordModal && (
                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                                <div className="absolute inset-0" onClick={() => setShowPasswordModal(false)} />
                                <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-scale-in">
                                    <div className="flex items-center justify-between mb-5">
                                        <h3 className="text-lg font-semibold text-gray-900">{hasPassword ? '修改密码' : '设置密码'}</h3>
                                        <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">验证手机号</label>
                                            <div className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600 bg-gray-50">
                                                {hasBoundPhone ? contactInfo.phone : '请先绑定手机号'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                                            <div className="relative">
                                                <input
                                                    type={showNewPw ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="请输入新密码（至少6位）"
                                                    className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400"
                                                />
                                                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">确认密码</label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPw ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="请再次输入密码"
                                                    className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400"
                                                />
                                                <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                    {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">短信验证码</label>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="text"
                                                    value={passwordVerificationCode}
                                                    onChange={(e) => setPasswordVerificationCode(e.target.value)}
                                                    placeholder="请输入短信验证码"
                                                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={!hasBoundPhone || passwordCodeSending || passwordCodeCountdown > 0}
                                                    onClick={async () => {
                                                        if (!hasBoundPhone) {
                                                            showToast('请先绑定手机号后再设置密码', 'error');
                                                            return;
                                                        }
                                                        setPasswordCodeSending(true);
                                                        try {
                                                            const res = await userService.sendPasswordVerificationCode();
                                                            if (res.success) {
                                                                showToast('验证码已发送，请查收短信', 'success');
                                                                setPasswordCodeCountdown(60);
                                                            } else {
                                                                showToast(res.error || '验证码发送失败，请稍后重试', 'error');
                                                            }
                                                        } catch {
                                                            showToast('验证码发送失败，请检查网络', 'error');
                                                        } finally {
                                                            setPasswordCodeSending(false);
                                                        }
                                                    }}
                                                    className="shrink-0 rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {passwordCodeCountdown > 0
                                                        ? `${passwordCodeCountdown}s 后重试`
                                                        : (passwordCodeSending ? '发送中...' : '获取验证码')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            if (!hasBoundPhone) {
                                                showToast('请先绑定手机号后再设置密码', 'error');
                                                return;
                                            }
                                            if (newPassword.length < 6) {
                                                showToast('密码至少需要6位', 'error');
                                                return;
                                            }
                                            if (newPassword !== confirmPassword) {
                                                showToast('两次输入的密码不一致', 'error');
                                                return;
                                            }
                                            if (!passwordVerificationCode.trim()) {
                                                showToast('请输入短信验证码', 'error');
                                                return;
                                            }
                                            setPasswordSaving(true);
                                            try {
                                                const res = await userService.updatePassword(newPassword, passwordVerificationCode.trim());
                                                if (res.success) {
                                                    showToast(hasPassword ? '密码修改成功' : '密码设置成功', 'success');
                                                    setHasPassword(true);
                                                    setPasswordVerificationCode('');
                                                    setShowPasswordModal(false);
                                                } else {
                                                    showToast(res.error || '设置失败，请重试', 'error');
                                                }
                                            } catch {
                                                showToast('设置失败，请检查网络', 'error');
                                            } finally {
                                                setPasswordSaving(false);
                                            }
                                        }}
                                        disabled={passwordSaving}
                                        className="w-full mt-5 bg-black text-white rounded-xl py-3 text-sm font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        {passwordSaving ? '保存中...' : '确认'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {showDeleteAccountModal && (
                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                                <div className="absolute inset-0" onClick={() => setShowDeleteAccountModal(false)} />
                                <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-scale-in">
                                    <div className="flex items-center justify-between mb-5">
                                        <h3 className="text-lg font-semibold text-gray-900">申请注销账号</h3>
                                        <button onClick={() => setShowDeleteAccountModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 mb-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                                            <div className="text-sm text-red-700 leading-6">
                                                <p>提交验证码后，账号会进入 3 天冷静期。</p>
                                                <p>到期后注销正式生效，届时将无法继续访问当前账号。</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">验证手机号</label>
                                            <div className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600 bg-gray-50">
                                                {hasBoundPhone ? contactInfo.phone : '请先绑定手机号'}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">短信验证码</label>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="text"
                                                    value={accountDeletionVerificationCode}
                                                    onChange={(e) => setAccountDeletionVerificationCode(e.target.value)}
                                                    placeholder="请输入短信验证码"
                                                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={!hasBoundPhone || accountDeletionCodeSending || accountDeletionCodeCountdown > 0}
                                                    onClick={async () => {
                                                        if (!hasBoundPhone) {
                                                            showToast('请先绑定手机号后再申请注销', 'error');
                                                            return;
                                                        }

                                                        setAccountDeletionCodeSending(true);
                                                        try {
                                                            const res = await userService.sendAccountDeletionVerificationCode();
                                                            if (res.success) {
                                                                showToast('验证码已发送，请查收短信', 'success');
                                                                setAccountDeletionCodeCountdown(60);
                                                            } else {
                                                                showToast(res.error || '验证码发送失败，请稍后重试', 'error');
                                                            }
                                                        } catch {
                                                            showToast('验证码发送失败，请检查网络', 'error');
                                                        } finally {
                                                            setAccountDeletionCodeSending(false);
                                                        }
                                                    }}
                                                    className="shrink-0 rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {accountDeletionCodeCountdown > 0
                                                        ? `${accountDeletionCodeCountdown}s 后重试`
                                                        : (accountDeletionCodeSending ? '发送中...' : '获取验证码')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={async () => {
                                            if (!hasBoundPhone) {
                                                showToast('请先绑定手机号后再申请注销', 'error');
                                                return;
                                            }

                                            if (!accountDeletionVerificationCode.trim()) {
                                                showToast('请输入短信验证码', 'error');
                                                return;
                                            }

                                            setAccountDeletionSubmitting(true);
                                            try {
                                                const res = await userService.requestAccountDeletion(accountDeletionVerificationCode.trim());
                                                if (res.success && res.data?.effectiveAt) {
                                                    setAccountStatus('pending_deletion');
                                                    setDeletionEffectiveAt(res.data.effectiveAt);
                                                    setAccountDeletionVerificationCode('');
                                                    setShowDeleteAccountModal(false);
                                                    showToast(`注销申请已提交，将于 ${formatDeletionTime(res.data.effectiveAt)} 生效`, 'success');
                                                } else {
                                                    showToast(res.error || '注销申请提交失败，请稍后重试', 'error');
                                                }
                                            } catch {
                                                showToast('注销申请提交失败，请检查网络', 'error');
                                            } finally {
                                                setAccountDeletionSubmitting(false);
                                            }
                                        }}
                                        disabled={accountDeletionSubmitting}
                                        className="w-full mt-5 bg-red-600 text-white rounded-xl py-3 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                                    >
                                        {accountDeletionSubmitting ? '提交中...' : '确认申请注销'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* GENERAL TAB */}
                        {activeTab === 'general' && (
                            <div className="animate-fade-in">
                                <h2 className="text-xl font-bold mb-6">通用设置</h2>
                                <div className="space-y-6 max-w-lg">

                                    {/* Language */}
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">界面语言</label>
                                        <div className="relative">
                                            <select
                                                value={language}
                                                onChange={(e) => setLanguage(e.target.value)}
                                                className="w-full appearance-none px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black"
                                            >
                                                <option value="zh">简体中文</option>
                                                <option value="en">English</option>
                                                <option value="ja">日本語</option>
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                        </div>
                                    </div>

                                    {/* Theme */}
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">显示模式</label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <button
                                                onClick={() => setTheme('light')}
                                                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${theme === 'light' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                                            >
                                                <div className="w-full h-12 bg-white rounded border border-gray-200 shadow-sm mb-2"></div>
                                                <span className="text-sm font-medium">浅色模式</span>
                                            </button>
                                            <button
                                                onClick={() => setTheme('dark')}
                                                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${theme === 'dark' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                                            >
                                                <div className="w-full h-12 bg-gray-900 rounded border border-gray-800 shadow-sm mb-2"></div>
                                                <span className="text-sm font-medium">深色模式</span>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* PAYMENT TAB */}
                        {activeTab === 'payment' && (
                            <div className="animate-fade-in">
                                <PaymentSettings onSwitchToVerification={() => setActiveTab('verification')} />
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
